import { type ReactNode, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Form, Input, Modal, message } from 'antd';
import axios from 'axios';
import type { Member } from '../../types/domain';
import { useMembersData } from '../../hooks/data/useMembersData';

type Props = {
  user: Member | undefined;
  isVisible: boolean;
  onClose: () => void;
};

const EditMemberModal = ({ user, isVisible, onClose }: Props): ReactNode => {
  const { t } = useTranslation();
  const [form] = Form.useForm<{ name: string }>();
  const { updateMember } = useMembersData();

  useEffect((): void => {
    if (!isVisible) {
      return;
    }
    form.setFieldsValue({ name: user?.name });
  }, [isVisible, form, user?.name]);

  const handleClickUpdateMember = async (): Promise<void> => {
    const userId = user?.user_id;
    if (!userId) {
      return;
    }
    try {
      const submitValues = await form.validateFields();

      onClose();

      const messageKey = 'update-member-message';
      message.loading({
        key: messageKey,
        content: t('message.updatingMember'),
        duration: 0,
      });

      updateMember(userId, { name: submitValues.name })
        .then((): void => {
          message.success({
            key: messageKey,
            content: t('message.memberUpdated'),
            duration: 4,
          });
        })
        .catch((error: unknown): void => {
          let msg = t('message.failedToUpdateMember');
          if (axios.isAxiosError(error) && error.response?.data?.message) {
            msg += ` (${error.response.data.message})`;
          }
          message.error({
            key: messageKey,
            content: msg,
            duration: 4,
          });
        });
    } catch {
      // バリデーションエラー時は何もしない
    }
  };

  return (
    <Modal
      title={t('editMember')}
      cancelText={t('cancel')}
      okText={t('submit')}
      visible={isVisible}
      onCancel={onClose}
      onOk={handleClickUpdateMember}
      forceRender
      centered
    >
      <StyledForm
        form={form}
        name="add-member"
        labelCol={{ span: 6 }}
        wrapperCol={{ span: 12 }}
        autoComplete="off"
      >
        <Form.Item
          label={t('memberName')}
          name="name"
          rules={[{ required: true, message: t('message.nameRequired') }]}
        >
          <Input size="small" />
        </Form.Item>
      </StyledForm>
    </Modal>
  );
};

const StyledForm = styled(Form)`
  .ant-form-item {
    margin-bottom: 12px;
  }
`;

export { EditMemberModal };
